import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  Dispatch,
  SetStateAction,
  useRef,
} from 'react';

interface AppContextType {
  isChatbotOpen: boolean;
  setIsChatbotOpen: (isOpen: boolean) => void;
  isAliciaChatbotMenuOpen: boolean;
  setIsAliciaChatbotMenuOpen: Dispatch<SetStateAction<boolean>>;
  isMenuOpen: boolean;
  setIsMenuOpen: Dispatch<SetStateAction<boolean>>;
  isNotificationDrawerOpen: boolean;
  setIsNotificationDrawerOpen: (isOpen: boolean) => void;
  isModuleDrawerOpen: boolean;
  setIsModuleDrawerOpen: (isOpen: boolean) => void;
  handleOpenAliciaChatbot: () => void;
  handleOpenMenu: () => void;
  handleCloseMenu: () => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

interface AppProviderProps {
  children: ReactNode;
}

type PreviousView = 'alicia' | null;

export const AppProvider = ({ children }: AppProviderProps): React.JSX.Element => {
  const [isChatbotOpen, setIsChatbotOpen] = useState<boolean>(false);
  const [isAliciaChatbotMenuOpen, setIsAliciaChatbotMenuOpen] = useState<boolean>(false);
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [isNotificationDrawerOpen, setIsNotificationDrawerOpen] = useState<boolean>(false);
  // Set by a module's own drawer (PAS); apart from the bell's flag, which the bell resets on unmount.
  const [isModuleDrawerOpen, setIsModuleDrawerOpen] = useState<boolean>(false);
  const previousViewRef = useRef<PreviousView>(null);

  const handleOpenAliciaChatbot = useCallback(() => {
    setIsAliciaChatbotMenuOpen(true);
    setIsMenuOpen(false);
    // Switching to the Alicia view always leaves the notifications drawer behind.
    setIsNotificationDrawerOpen(false);
  }, [setIsNotificationDrawerOpen]);

  const handleOpenMenu = useCallback(() => {
    // Save which view was active before opening menu
    if (isAliciaChatbotMenuOpen) {
      previousViewRef.current = 'alicia';
    } else {
      previousViewRef.current = null; // was on home
    }

    // Close all other views and open menu
    setIsAliciaChatbotMenuOpen(false);
    setIsMenuOpen(true);
    // Switching to the Menu view always leaves the notifications drawer behind.
    setIsNotificationDrawerOpen(false);
  }, [isAliciaChatbotMenuOpen, setIsNotificationDrawerOpen]);

  const handleCloseMenu = useCallback(() => {
    setIsMenuOpen(false);

    // Restore previous view
    if (previousViewRef.current === 'alicia') {
      setIsAliciaChatbotMenuOpen(true);
    }
    // If it was null (home), do nothing - home is the default

    // Reset the saved state
    previousViewRef.current = null;
  }, []);

  return (
    <AppContext.Provider
      value={{
        isChatbotOpen,
        setIsChatbotOpen,
        isAliciaChatbotMenuOpen,
        setIsAliciaChatbotMenuOpen,
        isMenuOpen,
        setIsMenuOpen,
        isNotificationDrawerOpen,
        setIsNotificationDrawerOpen,
        isModuleDrawerOpen,
        setIsModuleDrawerOpen,
        handleOpenAliciaChatbot,
        handleOpenMenu,
        handleCloseMenu,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useApp = (): AppContextType => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};
